import React, { useState, useRef } from 'react';
import { useProjectStore } from '../../stores/projectStore';

interface ProjectManagerProps {
  isOpen: boolean;
  onClose: () => void;
  palette: {
    panelBg: string;
    panelBorder: string;
    panelText: string;
    panelSubText: string;
  };
  glass: React.CSSProperties;
}

function formatDate(ts: number) {
  return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function ProjectManager({ isOpen, onClose, palette, glass }: ProjectManagerProps) {
  const {
    projects, activeProjectId,
    createProject, deleteProject, renameProject, updateDescription, switchProject,
    importProjectFromJson, addCheckpoint, restoreCheckpoint, deleteCheckpoint,
    saveActiveProject,
  } = useProjectStore();

  const [tab, setTab] = useState<'projects' | 'checkpoints'>('projects');
  const [newName, setNewName] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameVal, setRenameVal] = useState('');
  const [editingDescId, setEditingDescId] = useState<string | null>(null);
  const [descVal, setDescVal] = useState('');
  const [checkpointLabel, setCheckpointLabel] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const activeProject = projects.find(p => p.id === activeProjectId);

  const handleCreate = () => {
    const name = newName.trim() || `Project ${projects.length + 1}`;
    createProject(name);
    setNewName('');
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const json = ev.target?.result as string;
      const name = file.name.replace(/\.(velostruct|velostruct|json)$/i, '') || file.name;
      try {
        importProjectFromJson(name, json);
        onClose();
      } catch {
        alert('Failed to load file — invalid format.');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleAddCheckpoint = () => {
    const label = checkpointLabel.trim() || `Checkpoint ${(activeProject?.checkpoints.length ?? 0) + 1}`;
    addCheckpoint(label);
    setCheckpointLabel('');
  };

  const btnBase = 'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors';

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative z-10 w-[600px] max-h-[80vh] flex flex-col rounded-2xl shadow-2xl border overflow-hidden"
        style={{ ...glass, maxHeight: '80vh' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b flex-shrink-0" style={{ borderColor: palette.panelBorder }}>
          <span className="text-base font-semibold" style={{ color: palette.panelText }}>Projects</span>
          <div className="flex items-center gap-2">
            <button
              className={btnBase}
              style={{ background: tab === 'projects' ? '#4f46e5' : 'transparent', color: tab === 'projects' ? 'white' : palette.panelSubText }}
              onClick={() => setTab('projects')}
            >Projects</button>
            <button
              className={btnBase}
              style={{ background: tab === 'checkpoints' ? '#4f46e5' : 'transparent', color: tab === 'checkpoints' ? 'white' : palette.panelSubText }}
              onClick={() => setTab('checkpoints')}
            >Checkpoints</button>
            <button onClick={onClose} className="ml-2 text-lg leading-none" style={{ color: palette.panelSubText }}>✕</button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {tab === 'projects' && (
            <>
              {/* New project */}
              <div className="flex gap-2">
                <input
                  className="flex-1 bg-white/5 border rounded-lg px-3 py-2 text-sm focus:outline-none"
                  style={{ color: palette.panelText, borderColor: palette.panelBorder }}
                  placeholder="New project name…"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleCreate()}
                />
                <button
                  className={btnBase + ' flex-shrink-0'}
                  style={{ background: '#4f46e5', color: 'white' }}
                  onClick={handleCreate}
                >+ New</button>
                <button
                  className={btnBase + ' flex-shrink-0'}
                  style={{ background: 'transparent', color: palette.panelSubText, border: `1px solid ${palette.panelBorder}` }}
                  onClick={() => fileInputRef.current?.click()}
                >↑ Upload</button>
                <input ref={fileInputRef} type="file" accept=".velostruct,.velostruct,.json" className="hidden" onChange={handleFileUpload} />
              </div>

              {/* Project list */}
              {projects.length === 0 && (
                <p className="text-center py-8 text-sm" style={{ color: palette.panelSubText }}>No projects yet. Create one above.</p>
              )}
              {projects.map(p => {
                const isActive = p.id === activeProjectId;
                return (
                  <div
                    key={p.id}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl border transition-all"
                    style={{
                      borderColor: isActive ? '#4f46e5' : palette.panelBorder,
                      background: isActive ? '#4f46e520' : 'transparent',
                    }}
                  >
                    <div className="flex-1 min-w-0">
                      {renamingId === p.id ? (
                        <input
                          autoFocus
                          className="w-full bg-white/10 border rounded px-2 py-0.5 text-sm focus:outline-none"
                          style={{ color: palette.panelText, borderColor: palette.panelBorder }}
                          value={renameVal}
                          onChange={e => setRenameVal(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') { renameProject(p.id, renameVal.trim() || p.name); setRenamingId(null); }
                            if (e.key === 'Escape') setRenamingId(null);
                          }}
                          onBlur={() => { renameProject(p.id, renameVal.trim() || p.name); setRenamingId(null); }}
                        />
                      ) : (
                        <div className="flex items-center gap-2">
                          {isActive && <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />}
                          <span className="text-sm font-medium truncate" style={{ color: palette.panelText }}>{p.name}</span>
                        </div>
                      )}
                      {/* Description — inline edit on click */}
                      {editingDescId === p.id ? (
                        <input
                          autoFocus
                          className="w-full bg-white/10 border rounded px-2 py-0.5 text-xs focus:outline-none mt-0.5"
                          style={{ color: palette.panelSubText, borderColor: palette.panelBorder }}
                          value={descVal}
                          placeholder="Add a description…"
                          onChange={e => setDescVal(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') { updateDescription(p.id, descVal.trim()); setEditingDescId(null); }
                            if (e.key === 'Escape') setEditingDescId(null);
                          }}
                          onBlur={() => { updateDescription(p.id, descVal.trim()); setEditingDescId(null); }}
                        />
                      ) : (
                        <p
                          className="text-xs mt-0.5 cursor-text truncate"
                          style={{ color: p.description ? palette.panelSubText : palette.panelBorder }}
                          onClick={() => { setEditingDescId(p.id); setDescVal(p.description ?? ''); }}
                          title="Click to edit description"
                        >
                          {p.description || 'Add description…'}
                        </p>
                      )}
                      <span className="text-xs" style={{ color: palette.panelBorder }}>
                        {p.nodeCount !== undefined ? `${p.nodeCount} nodes · ${p.edgeCount} edges · ` : ''}{p.checkpoints.length} checkpoint{p.checkpoints.length !== 1 ? 's' : ''} · {formatDate(p.updatedAt)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {!isActive && (
                        <button
                          className={btnBase}
                          style={{ background: '#4f46e5', color: 'white' }}
                          onClick={() => { switchProject(p.id); onClose(); }}
                        >Open</button>
                      )}
                      {isActive && (
                        <button
                          className={btnBase}
                          style={{ background: 'transparent', color: '#10b981', border: '1px solid #10b98140' }}
                          onClick={() => { saveActiveProject(); }}
                        >Save</button>
                      )}
                      <button
                        className={btnBase}
                        style={{ background: 'transparent', color: palette.panelSubText, border: `1px solid ${palette.panelBorder}` }}
                        onClick={() => { setRenamingId(p.id); setRenameVal(p.name); }}
                      >✏</button>
                      <button
                        className={btnBase}
                        style={{ background: 'transparent', color: '#ef4444', border: '1px solid #ef444440' }}
                        onClick={() => {
                          if (projects.length === 1) { alert("Can't delete the only project."); return; }
                          if (window.confirm(`Delete "${p.name}"?`)) deleteProject(p.id);
                        }}
                      >✕</button>
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {tab === 'checkpoints' && (
            <>
              {/* Add checkpoint */}
              <div className="flex gap-2">
                <input
                  className="flex-1 bg-white/5 border rounded-lg px-3 py-2 text-sm focus:outline-none"
                  style={{ color: palette.panelText, borderColor: palette.panelBorder }}
                  placeholder="Checkpoint label…"
                  value={checkpointLabel}
                  onChange={e => setCheckpointLabel(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAddCheckpoint()}
                />
                <button
                  className={btnBase + ' flex-shrink-0'}
                  style={{ background: '#4f46e5', color: 'white' }}
                  onClick={handleAddCheckpoint}
                >+ Checkpoint</button>
              </div>
              <p className="text-xs" style={{ color: palette.panelSubText }}>
                Checkpoints for: <strong style={{ color: palette.panelText }}>{activeProject?.name ?? '—'}</strong> (max 5, oldest auto-removed)
              </p>

              {(!activeProject || activeProject.checkpoints.length === 0) && (
                <p className="text-center py-8 text-sm" style={{ color: palette.panelSubText }}>No checkpoints yet.</p>
              )}
              {activeProject && [...activeProject.checkpoints].reverse().map((cp, i) => (
                <div
                  key={cp.id}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl border"
                  style={{ borderColor: palette.panelBorder }}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono" style={{ color: '#818cf8' }}>#{activeProject.checkpoints.length - i}</span>
                      <span className="text-sm font-medium" style={{ color: palette.panelText }}>{cp.label}</span>
                    </div>
                    <span className="text-xs" style={{ color: palette.panelSubText }}>{formatDate(cp.createdAt)}</span>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      className={btnBase}
                      style={{ background: '#4f46e5', color: 'white' }}
                      onClick={() => {
                        if (window.confirm(`Restore checkpoint "${cp.label}"? Current state will be saved to undo history.`)) {
                          restoreCheckpoint(activeProjectId!, cp.id);
                          onClose();
                        }
                      }}
                    >Restore</button>
                    <button
                      className={btnBase}
                      style={{ background: 'transparent', color: '#ef4444', border: '1px solid #ef444440' }}
                      onClick={() => deleteCheckpoint(activeProjectId!, cp.id)}
                    >✕</button>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
